import React, { useContext } from "react";
import Select from "react-select";
import { useSelector } from "react-redux";
import { loadFont } from "../../utils";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faAlignCenter,
  faAlignLeft,
  faAlignRight,
  faItalic,
  faUnderline,
} from "@fortawesome/free-solid-svg-icons";
import { CanvasContext } from "./CanvasContext";

const fontWeightOptions = [100, 400, 500, 700, 800].map((font) => ({
  value: font,
  label: font,
}));

const TextTools = ({ show, position, applyColor }) => {
  const handleToggleUnderline = () => {
    const activeObject = canvas.getActiveObject();
    if (activeObject) {
      applyColor(activeObject.underline ? "none" : "underline", "underline");
    }
  };

  const fontList = useSelector((state) => state.fonts);
  // Transform font list to format required by react-select
  const fontOptions = fontList.map((font) => ({
    value: font.name,
    label: font.name,
    fontFile: font.fontFile,
    customAbbreviation: font.fontBackground,
  }));

  const { canvas } = useContext(CanvasContext);
  const activeObject = canvas.getActiveObject();

  if (!show) {
    return null;
  }

  const style = {
    display: "block",
    position: "absolute",
    top: `${position.y}px`,
    left: `${position.x}px`,
    zIndex: 1000,
  };

  // Custom option renderer for react-select
  const formatOptionLabel = ({ value, label, customAbbreviation }) => (
    <div style={{ display: "flex", alignItems: "center" }}>
      <img
        src={customAbbreviation}
        style={{
          width: "100%",
          height: "30px",
          margin: "-5px",
          marginLeft: "0px",
        }}
        alt={label}
      />
    </div>
  );

  const onFontFamilyChange = async (selectedOption) => {
    const fontUrl = selectedOption.fontFile; // URL of the TTF file
    const fontName = selectedOption.label; // Name of the font
    await loadFont(fontName, fontUrl);
    applyColor(fontName, "fontFamily");
  };

  const {
    name,
    underline,
    fontWeight,
    fontFamily,
    fill,
    type,
    textAlign,
    fontStyle,
    fontSize,
    opacity,
    stroke,
    strokeWidth,
  } = activeObject || {};

  return type == "graphics" ? (
    <div
      style={style}
      className="w-64 bg-gray-200 p-4 rounded-lg shadow-lg text-xs"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex flex-wrap items-center mx-2 mb-4">
        <div className="w-1/2 px-2">
          <label className="block text-sm font-medium">Asset Color:</label>
        </div>
        <div className="w-1/2 px-2">
          <input
            type="color"
            className="border rounded-full w-8 h-9 overflow-hidden"
            value={
              (name || "").toLowerCase().includes("outline") ? stroke : fill
            }
            // style={{ padding: "-10px" }}
            onChange={(e) => applyColor(e.target.value)}
          />
        </div>
      </div>
      <div className="flex flex-wrap items-center mx-2 mb-4">
        <div className="w-1/2 px-2">
          <label className="block text-sm font-medium">Opacity:</label>
        </div>
        <div className="w-1/2 border-2 border-gray-400 relative">
          <input
            className="range vb-violet absolute inset-0 w-full h-full"
            type="range"
            defaultValue={`${opacity * 100}`}
            min={"0"}
            max={"100"}
            onChange={(e) => {
              applyColor(+e.target.value / 100, "opacity");
            }}
          />
        </div>
      </div>

      <div className="flex flex-wrap items-center mx-2 mb-4">
        <div className="w-1/2 px-2">
          <label className="block text-sm font-medium">Stroke Width:</label>
        </div>
        <div className="w-1/2 border-2 border-gray-400 relative">
          <input
            className="range vb-violet absolute inset-0 w-full h-full"
            type="range"
            defaultValue={`${strokeWidth || 1}`}
            min={"0"}
            max={"5"}
            onChange={(e) => {
              applyColor(+e.target.value, "strokeWidth");
            }}
          />
        </div>
      </div>
    </div>
  ) : (
    <>
      <div
        style={style}
        className="w-64 bg-gray-200 p-4 rounded-lg shadow-lg text-xs"
        onClick={(e) => e.stopPropagation()}
      >
        {/**font-family */}
        <div className="flex flex-wrap items-center -mx-2 mb-4">
          <div className="w-1/2 px-2">
            <label className="block text-sm font-medium">Font Family:</label>
          </div>
          <div className="w-1/2 px-2">
            <Select
              options={fontOptions}
              formatOptionLabel={formatOptionLabel}
              onChange={onFontFamilyChange}
              value={fontOptions.find((option) => option.value === fontFamily)}
            />
          </div>
        </div>

        {/**font-weight */}
        <div className="flex flex-wrap items-center -mx-2 mb-4">
          <div className="w-1/2 px-2">
            <label className="block text-sm font-medium">Font Weight:</label>
          </div>
          <div className="w-1/2 px-2">
            <Select
              options={fontWeightOptions}
              onChange={(option) => applyColor(option.value, "fontWeight")}
              value={fontWeightOptions.find(
                (option) => option.value === fontWeight
              )}
            />
          </div>
        </div>

        {/**font-size */}
        <div className="flex flex-wrap items-center -mx-2 mb-4">
          <div className="w-1/2 px-2">
            <label className="block text-sm font-medium">Font Size:</label>
          </div>
          <div className="w-1/2 px-2">
            <input
              type="number"
              className="border rounded p-1 w-full"
              defaultValue={+fontSize}
              onChange={(e) => applyColor(e.target.value, "fontSize")}
            />
          </div>
        </div>

        {/**font-color */}
        <div className="flex flex-wrap items-center -mx-2 mb-4">
          <div className="w-1/2 px-2">
            <label className="block text-sm font-medium">Font Color:</label>
          </div>
          <div className="w-1/2 px-2">
            <input
              type="color"
              className="border rounded-full w-8 h-9 overflow-hidden"
              value={fill}
              style={{ padding: "-10px" }}
              onChange={(e) => applyColor(e.target.value)}
            />
          </div>
        </div>

        {/**opacity */}
        <div className="flex flex-wrap items-center -mx-2 mb-4">
          <div className="w-1/2 px-2">
            <label className="block text-sm font-medium">Opacity:</label>
          </div>
          <div className="w-1/2 border-2 border-gray-400 relative">
            <input
              className="range vb-violet absolute inset-0 w-full h-full"
              type="range"
              defaultValue={`${opacity * 100}`}
              min={"0"}
              max={"100"}
              onChange={(e) => {
                applyColor(+e.target.value / 100, "opacity");
              }}
            />
          </div>
        </div>

        {/* stroke width */}
        {type === "text" && (
          <div className="flex flex-wrap items-center mx-2 mb-4">
            <div className="w-1/2 px-2">
              <label className="block text-sm font-medium">Stroke Width:</label>
            </div>
            <div className="w-1/2 border-2 border-gray-400 relative">
              <input
                className="range vb-violet absolute inset-0 w-full h-full"
                type="range"
                defaultValue={`${strokeWidth || 1}`}
                min={"0"}
                max={"5"}
                onChange={(e) => {
                  applyColor(+e.target.value, "strokeWidth");
                }}
              />
            </div>
          </div>
        )}

        {/**font-style bold/italic */}
        <div className="flex flex-wrap items-center -mx-2 mb-4">
          <div className="w-1/2 px-2">
            <label className="block text-sm font-medium">Font Style:</label>
          </div>
          <div className="w-1/2 px-2 space-x-2">
            <FontAwesomeIcon
              icon={faUnderline}
              onClick={handleToggleUnderline}
              className={`cursor-pointer ${
                activeObject && activeObject.underline ? "text-blue-500" : ""
              }`}
            />
            <FontAwesomeIcon
              icon={faItalic}
              onClick={() =>
                applyColor(
                  fontStyle === "italic" ? "normal" : "italic",
                  "fontStyle"
                )
              }
              className={`cursor-pointer ${
                fontStyle === "italic" ? "text-blue-500" : ""
              }`}
            />
          </div>
        </div>

        <div className="flex flex-wrap items-center -mx-2 mb-4">
          <div className="w-1/2 px-2">
            <label className="block text-sm font-medium">Text Alignment:</label>
          </div>
          <div className="w-1/2 px-2">
            <div className="flex space-x-2">
              <FontAwesomeIcon
                icon={faAlignLeft}
                onClick={() => applyColor("left", "textAlign")}
                className={`cursor-pointer ${
                  textAlign === "left" && "text-blue-500"
                }`}
              />
              <FontAwesomeIcon
                icon={faAlignCenter}
                onClick={() => applyColor("center", "textAlign")}
                className={`cursor-pointer ${
                  textAlign === "center" && "text-blue-500"
                }`}
              />
              <FontAwesomeIcon
                icon={faAlignRight}
                onClick={() => applyColor("right", "textAlign")}
                className={`cursor-pointer ${
                  textAlign === "right" && "text-blue-500"
                }`}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default TextTools;
